/**
 * Document Requirements API Hooks
 *
 * React Query hooks for fetching and managing document type requirements.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api-client';
import type {
    MatrixResponse,
    RequirementResponse,
    RequirementCreate,
    RequirementUpdate,
    ConditionPreset,
    ConditionField,
} from '@/types/api/document-requirements';

// ============================================================================
// Query Keys
// ============================================================================

export const REQUIREMENTS_QUERY_KEYS = {
    all: ['document-requirements'] as const,
    matrix: () => [...REQUIREMENTS_QUERY_KEYS.all, 'matrix'] as const,
    presets: () => [...REQUIREMENTS_QUERY_KEYS.all, 'presets'] as const,
    statuses: () => [...REQUIREMENTS_QUERY_KEYS.all, 'statuses'] as const,
    conditionFields: () => [...REQUIREMENTS_QUERY_KEYS.all, 'condition-fields'] as const,
    forDocType: (docTypeId: string) => [...REQUIREMENTS_QUERY_KEYS.all, 'doc-type', docTypeId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/** Fetch the full requirements matrix (all doc types × all statuses) */
export function useRequirementsMatrix() {
    return useQuery({
        queryKey: REQUIREMENTS_QUERY_KEYS.matrix(),
        queryFn: () => apiGet<MatrixResponse>('/document-requirements/matrix'),
    });
}

/** Fetch condition presets */
export function useConditionPresets() {
    return useQuery({
        queryKey: REQUIREMENTS_QUERY_KEYS.presets(),
        queryFn: () => apiGet<{ presets: Record<string, ConditionPreset> }>('/document-requirements/presets'),
        staleTime: Infinity, // Presets don't change at runtime
    });
}

/** Fetch valid shipment statuses */
export function useRequirementStatuses() {
    return useQuery({
        queryKey: REQUIREMENTS_QUERY_KEYS.statuses(),
        queryFn: () => apiGet<{ statuses: string[] }>('/document-requirements/statuses'),
        staleTime: Infinity,
    });
}

/** Fetch available condition fields */
export function useConditionFields() {
    return useQuery({
        queryKey: REQUIREMENTS_QUERY_KEYS.conditionFields(),
        queryFn: () => apiGet<{ fields: ConditionField[] }>('/document-requirements/condition-fields'),
        staleTime: Infinity,
    });
}

/** Fetch requirements for a specific document type */
export function useDocTypeRequirements(docTypeId: string | undefined) {
    return useQuery({
        queryKey: REQUIREMENTS_QUERY_KEYS.forDocType(docTypeId || ''),
        queryFn: () =>
            apiGet<{ document_type_id: string; document_type_key: string; requirements: RequirementResponse[]; count: number }>(
                `/document-types/${docTypeId}/requirements`
            ),
        enabled: !!docTypeId,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/** Create a new requirement */
export function useCreateRequirement() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ docTypeId, data }: { docTypeId: string; data: RequirementCreate }) =>
            apiPost<RequirementResponse>(`/document-types/${docTypeId}/requirements`, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.matrix() });
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.forDocType(variables.docTypeId) });
        },
    });
}

/** Update an existing requirement */
export function useUpdateRequirement() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            docTypeId,
            requirementId,
            data,
        }: {
            docTypeId: string;
            requirementId: string;
            data: RequirementUpdate;
        }) => apiPut<RequirementResponse>(`/document-types/${docTypeId}/requirements/${requirementId}`, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.matrix() });
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.forDocType(variables.docTypeId) });
        },
    });
}

/** Delete a requirement */
export function useDeleteRequirement() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ docTypeId, requirementId }: { docTypeId: string; requirementId: string }) =>
            apiDelete(`/document-types/${docTypeId}/requirements/${requirementId}`),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.matrix() });
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.forDocType(variables.docTypeId) });
        },
    });
}

/** Delete ALL requirements for a document type (remove entire row) */
export function useDeleteAllRequirements() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ docTypeId }: { docTypeId: string }) =>
            apiDelete(`/document-types/${docTypeId}/requirements`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEYS.matrix() });
        },
    });
}
